/**
 * CartDrawer — vanilla custom element (no Lit render).
 *
 * Responsibilities:
 *  - Reactively toggle the `cart-drawer-open` class and `inert` attribute
 *    based on the `isCartOpen` signal.
 *  - Wire up the close button, outside-click, and cart-icon click.
 *  - Manage a11y (aria-hidden + inert on background sections).
 *
 * Intentionally does NOT use Lit so the Liquid-rendered children
 * (header, item list, footer) are never overwritten by a render pass.
 */

import { effect } from "@preact/signals-core";
import { isCartOpen, openCart, closeCart } from "../store/cart-store";

class CartDrawer extends HTMLElement {
  private _disposeEffect?: () => void;
  private _outsideClickRaf: number | null = null;
  private _handleOutsideClick = (event: MouseEvent) => {
    if (!isCartOpen.value) return;

    const target = event.target as Element;
    if (!target.closest("#cart-icon") && !target.closest("cart-drawer")) {
      closeCart();
    }
  };

  /** Attach the outside-click listener one frame after opening, so the click
   *  that opened the drawer (in the synchronous cart-icon case) can't
   *  immediately close it again. */
  private _attachOutsideClick() {
    if (this._outsideClickRaf !== null) return;
    this._outsideClickRaf = requestAnimationFrame(() => {
      this._outsideClickRaf = null;
      window.addEventListener("click", this._handleOutsideClick);
    });
  }

  private _detachOutsideClick() {
    if (this._outsideClickRaf !== null) {
      cancelAnimationFrame(this._outsideClickRaf);
      this._outsideClickRaf = null;
    }
    window.removeEventListener("click", this._handleOutsideClick);
  }

  connectedCallback() {
    // Close button (rendered in Liquid)
    const closeBtn = this.querySelector<HTMLElement>("#cart-drawer-close-x-lit");
    closeBtn?.addEventListener("click", (e) => {
      e.stopPropagation();
      closeCart();
    });

    // Continue shopping button (rendered in Liquid footer)
    const continueBtn = this.querySelector<HTMLElement>(
      "#continue-shopping-button-lit"
    );
    continueBtn?.addEventListener("click", () => closeCart());

    // Cart icon in the header (exists outside this element)
    const cartIcon = document.querySelector<HTMLElement>("#cart-icon");
    cartIcon?.addEventListener("click", () => openCart());

    // Reactive effect: open/close state → DOM
    this._disposeEffect = effect(() => {
      const open = isCartOpen.value;

      if (open) {
        this._attachOutsideClick();
        this.classList.add("cart-drawer-open");
        this.inert = false;
        this.removeAttribute("aria-hidden");
        this._setBackgroundHidden(true);
        (window as any).knocking?.showBlur?.();
        (window as any).knocking?.disableScroll?.();
      } else {
        this._detachOutsideClick();
        this.classList.remove("cart-drawer-open");
        this.inert = true;
        this._setBackgroundHidden(false);
        (window as any).knocking?.hideBlur?.();
        (window as any).knocking?.enableScroll?.();
      }
    });
  }

  disconnectedCallback() {
    this._disposeEffect?.();
    this._detachOutsideClick();
  }

  private _setBackgroundHidden(hide: boolean) {
    const selectors = [
      "#shopify-section-announcement-bar",
      "#shopify-section-header",

      "main#MainContent",
      "#shopify-section-footer",
    ];
    selectors.forEach((sel) => {
      const el = document.querySelector<HTMLElement>(sel);
      if (!el) return;
      if (hide) {
        el.setAttribute("aria-hidden", "true");
        try {
          el.inert = true;
        } catch (_) {}
      } else {
        el.removeAttribute("aria-hidden");
        try {
          el.inert = false;
        } catch (_) {}
      }
    });
  }
}

customElements.define("cart-drawer", CartDrawer);
